import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { Activity, useMemo, useState } from 'react';
import SSOAttributeRuleModal from './sso-attribute-rule-modal';
import DeleteRuleModal from './delete-rule-modal';
import useComputedRegistrationSettings from '../../../hooks/use-computed-registration-settings';
import useLoadSSOs from '../../../hooks/use-load-ssos';
import { SSOAttributeRule, SSOOptionsType } from '@/types/registration.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getUser } from '@/store/slices/user/selectors';
import TextAlert from '@ui/text-alert/text-alert';
import TextAlertTitle from '@ui/text-alert/text-alert-title';
import { getDefaultSSOAttributeRule, isValidAttributeRule } from '@/utils/authentication/sso-utils';
import RenderRule from './render-rule';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import IconWithBackdrop from '@ui/icons/icon-with-backdrop';
import Text from '@ui/text/text/text';

export default function AccessControlSettings() {
  const dispatch = useAppDispatch();

  const user = useAppSelector(getUser);
  const [selectedRuleToEdit, setSelectedRuleToEdit] = useState<SSOAttributeRule>(getDefaultSSOAttributeRule(user?.id));
  const [selectedRuleToDelete, setSelectedRuleToDelete] = useState<SSOAttributeRule | null>(null);
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const { registrationSettings } = useComputedRegistrationSettings();

  const { ssos } = useLoadSSOs();

  const selectedCustomSso = useMemo(() => {
    return ssos.find((sso: SSOOptionsType) => registrationSettings?.singleSignOn?.singleSignOns?.includes(sso.uuid));
  }, [registrationSettings?.singleSignOn?.singleSignOns, ssos]);

  const rules = useMemo(() => {
    return registrationSettings?.singleSignOn?.rules?.toSorted((a, b) => b.createdAt.localeCompare(a.createdAt)) ?? [];
  }, [registrationSettings?.singleSignOn?.rules]);

  const handleAddRule = () => {
    setSelectedRuleToEdit(getDefaultSSOAttributeRule(user?.id));
    setShowAddRuleModal(true);
  };

  const handleToggleRule = (uuid: string, on: boolean) => {
    if (!registrationSettings?.singleSignOn) return;
    const updatedRules = rules.map((rule) =>
      rule.uuid === uuid ? { ...rule, enabled: on, updated_at: new Date().toISOString(), updated_by: user?.id } : rule
    );
    dispatch(
      saveWorkingProjectByFields({
        registration_settings: {
          ...registrationSettings,
          singleSignOn: { ...registrationSettings?.singleSignOn, rules: updatedRules },
        },
      })
    );
  };

  const handleConfirmDeleteRule = () => {
    if (!registrationSettings?.singleSignOn || !selectedRuleToDelete) return;
    const updatedRules = rules.filter((rule) => rule.uuid !== selectedRuleToDelete.uuid);
    dispatch(
      saveWorkingProjectByFields({
        registration_settings: {
          ...registrationSettings,
          singleSignOn: { ...registrationSettings?.singleSignOn, rules: updatedRules },
        },
      })
    );
    setSelectedRuleToDelete(null);
  };

  const handleEditRule = (uuid: string) => {
    const rule = rules.find((rule) => rule.uuid === uuid);
    if (!rule) return;
    setSelectedRuleToEdit(rule);
    setShowAddRuleModal(true);
  };

  const handleEditRuleClose = () => {
    setSelectedRuleToEdit(getDefaultSSOAttributeRule(user?.id));
    setShowAddRuleModal(false);
  };

  const allRulesValid = useMemo(() => {
    return rules.every((rule) => selectedCustomSso && isValidAttributeRule(rule, selectedCustomSso));
  }, [rules, selectedCustomSso]);

  if (!selectedCustomSso) return null;
  return (
    <div className="flex-column gap-8">
      <div className="flex ai-center gap-8">
        <Text tag="h5" className="H5" design="GlobalIcoText1">
          Access Control
        </Text>
        <TooltipPortal tooltipContent={<Text design="GlobalIcoText1">Access Control</Text>}>
          <IconWithBackdrop
            name={ICONS.INFO_CIRCLE}
            size={12}
            color="var(--primitives-mode-1-gray-500)"
            backdropColor="var(--primitives-mode-1-gray-100)"
          />
        </TooltipPortal>
      </div>
      <Container
        design="GlobalCont1"
        borderRadius="12px"
        padding="16px"
        className="flex fd-column gap-8"
        border={allRulesValid ? 'none' : '1px solid var(--global-error-500)'}
      >
        <div className="flex ai-center jc-space-between">
          <Text tag="h6" className="H6">
            Rules & Conditions
          </Text>
          <div>
            <Button
              design="LocalBttnSec"
              designSize="small"
              className="flex ai-center jc-center gap-6"
              onClick={handleAddRule}
            >
              <Icon name={ICONS.ADD} size={12} />
              <Text>Add rule</Text>
            </Button>
          </div>
        </div>
        <Activity mode={allRulesValid ? 'hidden' : 'visible'}>
          <TextAlert severity="error">
            <TextAlertTitle>Invalid rules</TextAlertTitle>
            <Text className="C2">Your SSO Configuration has changed, invalidating your existing ruleset</Text>
          </TextAlert>
        </Activity>
        <OptionalComponent display={rules.length > 0}>
          <Text className="S1" design="GlobalIcoText3">
            Attribute Rules
          </Text>
          <>
            {rules.map((rule) => (
              <RenderRule
                key={rule.uuid}
                rule={rule}
                selectedCustomSso={selectedCustomSso}
                handleToggleRule={handleToggleRule}
                handleEditRule={handleEditRule}
                setSelectedRuleToDelete={setSelectedRuleToDelete}
              />
            ))}
          </>
        </OptionalComponent>
      </Container>
      <SSOAttributeRuleModal
        open={showAddRuleModal}
        onClose={handleEditRuleClose}
        existingRule={selectedRuleToEdit}
        selectedCustomSSO={selectedCustomSso as SSOOptionsType}
      />
      <DeleteRuleModal
        open={!!selectedRuleToDelete}
        onClose={() => setSelectedRuleToDelete(null)}
        onDelete={handleConfirmDeleteRule}
      />
    </div>
  );
}
